/**
 * Main navigation: desktop dropdowns, the mobile drawer, and the keyboard model
 * for both. One #mainNav serves as the desktop nav bar and the mobile drawer,
 * so every behaviour here has to be correct at both breakpoints.
 *
 * Keyboard model is the W3C APG disclosure-navigation pattern, not menubar:
 * Tab visits only the top-level items while everything is closed, Enter/Space
 * (native, the triggers are <button>s) or ArrowDown expands the focused item,
 * Escape closes it and puts focus back on the trigger, and tabbing past the
 * last child closes a desktop dropdown behind you. Links stay links, and Tab is
 * never hijacked.
 *
 * Tabbability is managed with the `inert` attribute rather than CSS. Closed
 * submenus and the shut drawer stay in the layout -- opacity 0 on desktop,
 * slid out to right:-100% on mobile -- so their links were focusable while
 * invisible. `inert` removes a subtree from the tab order and the
 * accessibility tree without touching anything that renders, so the slide and
 * fade transitions are unaffected.
 *
 * The state classes the Sass keys off are unchanged: `open` on .sub-menu,
 * .nav-wrapper and .mobile-nav-toggle, `subnav-open` on the trigger, and
 * `subnav-open` / `nav-open` on <html> and <body>.
 */

/** Elements that never make sense to mark inert. */
const NON_VISUAL = /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/;

function isEscape(event) {
    return event.key === 'Escape' || event.key === 'Esc';
}

/**
 * Set or clear `inert` via the attribute, so it also works where the property
 * isn't implemented.
 */
function setInert(el, inert) {
    if (el) {
        el.toggleAttribute('inert', inert);
    }
}

/**
 * Focus without letting the browser scroll the element into view. The drawer
 * and the mobile submenu panels are overflow-x: hidden, which still permits
 * programmatic scrolling, so focusing into one mid-slide can leave it scrolled
 * sideways out of frame.
 */
function focusQuietly(el) {
    if (el) {
        el.focus({ preventScroll: true });
    }
}

export function initNav() {
    const nav = document.querySelector('.header-nav');

    if (!nav) {
        return;
    }

    const drawer = nav.querySelector('#mainNav');
    const toggle = nav.querySelector('#navToggle');

    if (!drawer) {
        return;
    }

    const root = document.documentElement;
    const body = document.body;

    /**
     * True while the CSS is presenting #mainNav as the mobile drawer. Derived
     * from whether the hamburger renders -- it lives inside a .d-lg-none block
     * -- so this can never drift out of step with the breakpoint in the Sass.
     */
    function isDrawerMode() {
        return !!toggle && toggle.getClientRects().length > 0;
    }

    function isDrawerOpen() {
        return drawer.classList.contains('open');
    }

    // --- Submenus ----------------------------------------------------------

    const items = [];

    nav.querySelectorAll('.main-nav .menu-item-has-children').forEach(function (li) {
        const trigger = li.querySelector(':scope > button');
        const submenu = trigger && document.getElementById(trigger.getAttribute('aria-controls'));

        if (!trigger || !submenu) {
            return;
        }

        items.push({
            li: li,
            trigger: trigger,
            submenu: submenu,
            back: submenu.querySelector('.menu-item-back > button')
        });
    });

    function isOpen(item) {
        return item.submenu.classList.contains('open');
    }

    /**
     * While a submenu is open in drawer mode it is a full-screen panel over the
     * drawer, so the other top-level items and the utility links are hidden
     * behind it. Take them out of the tab order until the panel closes.
     */
    function syncPanelSiblings() {
        const open = isDrawerMode() ? items.filter(isOpen)[0] : null;

        items.forEach(function (item) {
            setInert(item.li, !!open && item !== open);
        });

        drawer.querySelectorAll('.mobile-utility').forEach(function (el) {
            setInert(el, !!open);
        });
    }

    function setExpanded(item, open) {
        item.submenu.classList.toggle('open', open);
        item.trigger.classList.toggle('subnav-open', open);
        item.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        setInert(item.submenu, !open);
    }

    function syncSubnavClass() {
        const anyOpen = items.some(isOpen);

        root.classList.toggle('subnav-open', anyOpen);
        body.classList.toggle('subnav-open', anyOpen);
    }

    /** Collapse every submenu, optionally sparing the one about to open. */
    function closeAll(except) {
        items.forEach(function (item) {
            if (item !== except && isOpen(item)) {
                closeItem(item, false);
            }
        });
    }

    function openItem(item) {
        closeAll(item);
        setExpanded(item, true);
        syncSubnavClass();
        syncPanelSiblings();
    }

    function closeItem(item, returnFocus) {
        // Move focus off anything inside before the submenu goes inert,
        // otherwise the browser drops it to <body> and the user is lost.
        if (returnFocus || item.submenu.contains(document.activeElement)) {
            focusQuietly(item.trigger);
        }

        setExpanded(item, false);
        syncSubnavClass();
        syncPanelSiblings();
    }

    function toggleItem(item) {
        if (isOpen(item)) {
            closeItem(item, false);
        } else {
            openItem(item);
        }
    }

    items.forEach(function (item) {
        // Establish the closed state, including inert, before any interaction.
        setExpanded(item, false);

        // Enter and Space arrive here too: the triggers are real <button>s.
        item.trigger.addEventListener('click', function (event) {
            event.preventDefault();
            toggleItem(item);
        });

        item.trigger.addEventListener('keydown', function (event) {
            if (event.key === 'ArrowDown' || event.key === 'Down') {
                event.preventDefault();

                if (!isOpen(item)) {
                    openItem(item);
                }

                // The mobile back button comes first in the DOM but is hidden on
                // desktop, so land on the first link that actually renders.
                const first = Array.prototype.filter.call(
                    item.submenu.querySelectorAll('a[href], button'),
                    function (el) { return el.getClientRects().length > 0; }
                )[0];

                focusQuietly(first);
            } else if (isEscape(event) && isOpen(item)) {
                event.preventDefault();
                closeItem(item, true);
            }
        });

        // Escape from anywhere inside the submenu returns to the trigger.
        item.submenu.addEventListener('keydown', function (event) {
            if (isEscape(event)) {
                event.preventDefault();
                closeItem(item, true);
            }
        });

        // Mobile back button: close this panel and return to its trigger.
        if (item.back) {
            item.back.addEventListener('click', function (event) {
                event.preventDefault();
                closeItem(item, true);
            });
        }

        // Tabbing or clicking out of the parent closes it behind you. A null
        // relatedTarget means focus left the document entirely (browser chrome,
        // another window), which is not a reason to collapse anything.
        item.li.addEventListener('focusout', function (event) {
            const to = event.relatedTarget;

            if (!to || item.li.contains(to)) {
                return;
            }

            // In drawer mode a submenu is a slide-over panel with its own back
            // button, so it waits for an explicit close instead.
            if (isDrawerMode()) {
                return;
            }

            if (isOpen(item)) {
                closeItem(item, false);
            }
        });
    });

    // --- Mobile drawer -----------------------------------------------------

    const header = nav.closest('header') || nav;

    /**
     * The regions sitting behind the drawer. It covers the viewport, so leaving
     * these tabbable means Tab walks into content the user cannot see.
     */
    function behindDrawer() {
        return Array.prototype.filter.call(body.children, function (el) {
            return el !== header && !NON_VISUAL.test(el.tagName);
        });
    }

    /**
     * #mainNav is only inert when it actually IS a drawer and it is shut. On
     * desktop the same element is the visible nav bar, so it must never be.
     */
    function syncDrawerInert() {
        setInert(drawer, isDrawerMode() && !isDrawerOpen());
    }

    function setDrawerOpen(open) {
        if (!open) {
            closeAll();

            // Same reason as closeItem: get focus out before it goes inert.
            if (toggle && drawer.contains(document.activeElement)) {
                focusQuietly(toggle);
            }
        } else {
            drawer.scrollLeft = 0;
        }

        drawer.classList.toggle('open', open);
        root.classList.toggle('nav-open', open);
        body.classList.toggle('nav-open', open);

        if (toggle) {
            toggle.classList.toggle('open', open);
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        }

        behindDrawer().forEach(function (el) {
            setInert(el, open);
        });

        syncDrawerInert();
    }

    if (toggle) {
        // Focus deliberately stays on the toggle when the drawer opens: #mainNav
        // follows it directly in the DOM, so Tab already lands inside the
        // drawer, and moving focus by script makes WebKit paint a focus ring
        // after an ordinary tap.
        toggle.addEventListener('click', function () {
            setDrawerOpen(!isDrawerOpen());
        });
    }

    // Escape closes the drawer, unless a submenu already consumed the key.
    nav.addEventListener('keydown', function (event) {
        if (!isEscape(event) || event.defaultPrevented) {
            return;
        }

        if (isDrawerMode() && isDrawerOpen()) {
            event.preventDefault();
            setDrawerOpen(false);
        }
    });

    // Close dropdowns on a click anywhere outside the nav.
    document.addEventListener('click', function (event) {
        if (!nav.contains(event.target)) {
            closeAll();
        }
    });

    // A width change can cross the breakpoint, so reset. Height-only resizes
    // (mobile browser toolbars showing/hiding while scrolling) are ignored, so
    // they don't snap an open drawer shut.
    let lastWidth = window.innerWidth;

    window.addEventListener('resize', function () {
        if (window.innerWidth === lastWidth) {
            return;
        }

        lastWidth = window.innerWidth;

        if (isDrawerOpen()) {
            setDrawerOpen(false);
        } else {
            closeAll();
        }

        syncDrawerInert();
    });

    syncDrawerInert();
}
